import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
} from "reactstrap";
import EditorFormSection from "../../EditorFormSection";
import FormattedNumber from "../../FormattedNumber";
import EditorFormHelpers from "../utils/FileUploadEditorFormHelpers";
import { Controller, useFormContext } from "react-hook-form";

const renderSizeOption = ({
  availableSize,
  width,
  height,
  language,
  displayPriceAmount,
  formState,
  onSizeClick,
}) => {
  const isSelected = availableSize[0] === width && availableSize[1] === height;
  const price = parseFloat(
    EditorFormHelpers.calculatePrice({
      ...formState,
      width: availableSize[0],
      height: availableSize[1],
    }).displayPrice * displayPriceAmount
  );

  return (
    <li
      className={`py-1 ${isSelected ? "selected" : ""}`}
      onClick={() => onSizeClick(availableSize[0], availableSize[1])}
    >
      <FormattedNumber
        value={Math.round(availableSize[0] * 100) / 100}
        language={language}
        unit="mm"
        decimals={0}
      />{" "}
      &nbsp;x&nbsp;
      <FormattedNumber
        value={Math.round(availableSize[1] * 100) / 100}
        language={language}
        unit="mm"
        decimals={0}
      />
      &nbsp;
      <span className="text-remark">
        (
        <FormattedNumber
          value={Math.round(availableSize[0] * 100) / 1000}
          language={language}
          unit="cm"
          decimals={1}
        />
        &nbsp;x&nbsp;
        <FormattedNumber
          value={Math.round(availableSize[1] * 100) / 1000}
          language={language}
          unit="cm"
          decimals={1}
        />
        )
      </span>
      <span className="text-success pull-right">
        <strong>
          <FormattedNumber
            value={parseFloat(
              EditorFormHelpers.calculatePrice({
                ...formState,
                width: availableSize[0],
                height: availableSize[1],
              }).displayPrice * displayPriceAmount
            )}
            language={language}
            unit="€"
            decimals={2}
          />
        </strong>
      </span>
    </li>
  );
};

const SizeSelectSection = (
  {
    isOpen,
    togglePanel,
    language,
    displayPriceAmount,
    availableSizes,
    showDefaultAmounts,
  },
  context
) => {
  const [sizeSelectModalIsOpen, setSizeSelectModalIsOpen] = useState(false);
  const toggleSizeSelectModal = () => {
    setSizeSelectModalIsOpen(!sizeSelectModalIsOpen);
  };

  const { control, watch, setValue } = useFormContext();
  const width = watch("width");
  const height = watch("height");
  const fileRatio = watch("fileRatio");
  const keepRatio = watch("keepRatio");
  const formState = watch();
  const changeSelectedSize = (width, height) => {
    setValue("width", width);
    setValue("height", height);
    setValue("fileRatio", height / width);
    setValue("keepRatio", true);

    // if (!sizeChanged) {
    //   setSizeChanged(true);
    // }

    setSizeSelectModalIsOpen(false);

    // // TODO if ratio missmatch start cropping
    // if (
    //   artwork?.data?.attributes?.filetype &&
    //   !cropping?.active &&
    //   (Math.round((width / height) * 10) !==
    //     Math.round(
    //       (artwork?.data?.attributes?.width /
    //         artwork?.data?.attributes?.height) *
    //         10
    //     ) ||
    //     width / height -
    //       artwork?.data?.attributes?.width / artwork?.data?.attributes?.height >
    //       0.001)
    // ) {
    //   toggleFileSizeModal();
    // }
  };

  return (
    <EditorFormSection
      help={context.t("FileEditor.dimensions.help")}
      panelName="size"
      panelTitle={context.t("Dimensions of your product")}
      open={isOpen}
      toggle={togglePanel}
    >
      {showDefaultAmounts && (
        <strong className="text-remark">
          {context.t("Displayed price for")} {displayPriceAmount}{" "}
          {context.t("units")}
        </strong>
      )}

      <Controller
        name="width"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            className={
              isNaN(width) || width < 10
                ? "is-invalid data-hj-allow"
                : "data-hj-allow"
            }
            min={10}
            type="hidden"
          />
        )}
      />
      <Controller
        name="height"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            className={
              isNaN(height) || height < 10
                ? "is-invalid data-hj-allow"
                : "data-hj-allow"
            }
            min={10}
            type="hidden"
          />
        )}
      />
      <ul className="sizeselect-list">
        {availableSizes[0].sizes.map((availableSize, index) =>
          renderSizeOption({
            availableSize,
            width,
            height,
            language,
            displayPriceAmount,
            formState,
            onSizeClick: changeSelectedSize,
          })
        )}
        {/* If Size is not in Topseller add it to list */}
        {!availableSizes[0].sizes.find((availableSize) => {
          return availableSize[0] === width && availableSize[1] === height;
        }) &&
          renderSizeOption({
            availableSize: [width, height],
            width,
            height,
            language,
            displayPriceAmount,
            formState,
            onSizeClick: changeSelectedSize,
          })}
      </ul>

      <Button onClick={toggleSizeSelectModal} size="sm" color="primary">
        {context.t("More Sizes")}
      </Button>

      <Modal
        fade={false}
        className="sizeselect-modal"
        isOpen={sizeSelectModalIsOpen}
        size="xl"
        toggle={toggleSizeSelectModal}
        centered
      >
        <ModalHeader>{context.t("Select available Size")}</ModalHeader>
        <ModalBody>
          <Row>
            {availableSizes.map((category, index) => (
              <Col key={`sizemodal-${index}`} md={4}>
                <strong>{context.t(category.title)}</strong>
                <ul className="sizeselect-list">
                  {category.sizes.map((availableSize, index) =>
                    renderSizeOption({
                      availableSize,
                      width,
                      height,
                      language,
                      displayPriceAmount,
                      formState,
                      onSizeClick: changeSelectedSize,
                    })
                  )}
                </ul>
              </Col>
            ))}
          </Row>
        </ModalBody>
      </Modal>
    </EditorFormSection>
  );
};

SizeSelectSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  togglePanel: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  displayPriceAmount: PropTypes.number.isRequired,
  availableSizes: PropTypes.array.isRequired,
  sizeSelectModalIsOpen: PropTypes.bool.isRequired,
  toggleSizeSelectModal: PropTypes.func.isRequired,
};

SizeSelectSection.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default SizeSelectSection;
